import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleNote } from '../CollapsibleNote';
import { useAnalyticsTechnicians } from '../../../hooks/useAnalyticsTechnicians';
import type { TechnicianRow } from '../../../services/analyticsService';
import './TechniciansTab.css';

interface TechniciansTabProps {
  year: number;
}

type SortColumn =
  | 'name'
  | 'reports'
  | 'asSupport'
  | 'travelDays'
  | 'workHours'
  | 'travelHours'
  | 'machinesServed'
  | 'partsSent'
  | 'partsUsed'
  | 'usedPct';
type SortDirection = 'asc' | 'desc' | null;

const COLUMNS: { key: SortColumn; labelKey: string }[] = [
  { key: 'name', labelKey: 'analytics.technicians.columns.name' },
  { key: 'reports', labelKey: 'analytics.technicians.columns.reports' },
  { key: 'asSupport', labelKey: 'analytics.technicians.columns.asSupport' },
  { key: 'travelDays', labelKey: 'analytics.technicians.columns.travelDays' },
  { key: 'workHours', labelKey: 'analytics.technicians.columns.workHours' },
  { key: 'travelHours', labelKey: 'analytics.technicians.columns.travelHours' },
  { key: 'machinesServed', labelKey: 'analytics.technicians.columns.machinesServed' },
  { key: 'partsSent', labelKey: 'analytics.technicians.columns.partsSent' },
  { key: 'partsUsed', labelKey: 'analytics.technicians.columns.partsUsed' },
  { key: 'usedPct', labelKey: 'analytics.technicians.columns.usedPct' },
];

export const TechniciansTab: React.FC<TechniciansTabProps> = ({ year }) => {
  const { t, i18n } = useTranslation();
  const { technicians, loading, error } = useAnalyticsTechnicians(year);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const numberFormatter = new Intl.NumberFormat(i18n.language);
  const hoursFormatter = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (loading) {
    return <p className="analytics-coming-soon">{t('app.loading')}</p>;
  }

  if (error || !technicians) {
    return <p className="analytics-error">{t('analytics.loadError')}</p>;
  }

  const handleSort = (column: SortColumn): void => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <span className="sort-icon sort-icon-default">⇅</span>;
    }
    if (sortDirection === 'asc') {
      return <span className="sort-icon sort-icon-asc">↑</span>;
    }
    if (sortDirection === 'desc') {
      return <span className="sort-icon sort-icon-desc">↓</span>;
    }
    return null;
  };

  const sortedRows: TechnicianRow[] = (() => {
    if (!sortColumn || !sortDirection) return technicians.technicians;
    const sorted = [...technicians.technicians].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return String(aValue).localeCompare(String(bValue));
      }
      return (aValue as number) - (bValue as number);
    });
    if (sortDirection === 'desc') sorted.reverse();
    return sorted;
  })();

  return (
    <div className="analytics-technicians">
      <CollapsibleNote title={t('analytics.technicians.attributionTitle')}>
        {t('analytics.technicians.attributionBanner')}
      </CollapsibleNote>

      {technicians.technicians.length === 0 ? (
        <p className="analytics-coming-soon">{t('analytics.technicians.noData')}</p>
      ) : (
        <div className="analytics-tech-table-container">
          <table className="analytics-tech-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={`sortable${col.key === 'name' ? '' : ' n'}`}
                    onClick={() => handleSort(col.key)}
                  >
                    {t(col.labelKey)} {renderSortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.userId}>
                  <td>{row.name}</td>
                  <td className="n">{numberFormatter.format(row.reports)}</td>
                  <td className="n">{numberFormatter.format(row.asSupport)}</td>
                  <td className="n">{numberFormatter.format(row.travelDays)}</td>
                  <td className="n">{hoursFormatter.format(row.workHours)}</td>
                  <td className="n">{hoursFormatter.format(row.travelHours)}</td>
                  <td className="n">{numberFormatter.format(row.machinesServed)}</td>
                  <td className="n">{numberFormatter.format(row.partsSent)}</td>
                  <td className="n">{numberFormatter.format(row.partsUsed)}</td>
                  <td className="n">{row.usedPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TechniciansTab;
